import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfig } from "../hooks/useConfig";
import { useNavigate } from "react-router-dom";
import API, { formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogOut, User, GraduationCap, Award, Briefcase, Calendar, Clock, Building2, Edit2, Check, X, Upload, CheckCircle2, Moon, Sun } from "lucide-react";
import ApplicationTimeline from "../components/ApplicationTimeline";
import CodeChallenge from "../components/CodeChallenge";
import InterviewChat from "../components/InterviewChat";

function getStatusStyle(status, isDark) {
  const map = {
    Pending: { background: 'rgba(234,179,8,0.12)', color: '#d97706', border: '1px solid rgba(234,179,8,0.25)' },
    Interested: { background: 'rgba(5,150,105,0.12)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Shortlisted: { background: 'rgba(59,130,246,0.12)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.25)' },
    "Interview Scheduled": { background: 'rgba(168,85,247,0.12)', color: '#9333ea', border: '1px solid rgba(168,85,247,0.25)' },
    "Offer Received": { background: 'rgba(5,150,105,0.12)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Rejected: { background: 'rgba(239,68,68,0.12)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.25)' },
  };
  if (isDark) {
    const darkMap = {
      Pending: { background: 'rgba(234,179,8,0.12)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.25)' },
      Interested: { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Shortlisted: { background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' },
      "Interview Scheduled": { background: 'rgba(168,85,247,0.12)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.25)' },
      "Offer Received": { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Rejected: { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' },
    };
    return darkMap[status] || { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' };
  }
  return map[status] || { background: 'rgba(0,0,0,0.04)', color: '#64748b', border: '1px solid rgba(0,0,0,0.1)' };
}

function getTagStyle(tag, isDark) {
  const s = {
    SWE:           { bg: isDark ? 'rgba(59,130,246,0.15)'  : '#dbeafe', text: isDark ? '#93c5fd' : '#1d4ed8', border: isDark ? 'rgba(59,130,246,0.3)'   : '#bfdbfe' },
    'Data Science':{ bg: isDark ? 'rgba(168,85,247,0.15)' : '#ede9fe', text: isDark ? '#c084fc' : '#7c3aed', border: isDark ? 'rgba(168,85,247,0.3)'  : '#ddd6fe' },
    Finance:       { bg: isDark ? 'rgba(5,150,105,0.15)'  : '#dcfce7', text: isDark ? '#34d399' : '#15803d', border: isDark ? 'rgba(5,150,105,0.3)'   : '#bbf7d0' },
    Consulting:    { bg: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7', text: isDark ? '#fbbf24' : '#b45309', border: isDark ? 'rgba(245,158,11,0.3)'  : '#fde68a' },
    Core:          { bg: isDark ? 'rgba(239,68,68,0.15)'  : '#fee2e2', text: isDark ? '#f87171' : '#b91c1c', border: isDark ? 'rgba(239,68,68,0.3)'   : '#fecaca' },
    Design:        { bg: isDark ? 'rgba(236,72,153,0.15)' : '#fce7f3', text: isDark ? '#f472b6' : '#be185d', border: isDark ? 'rgba(236,72,153,0.3)'  : '#fbcfe8' },
  };
  return s[tag] || { bg: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', text: isDark ? '#94a3b8' : '#475569', border: isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0' };
}

function getCompanyMatches(profile, companies) {
  const skills = (profile?.extracted_skills || []).map(s => s.toLowerCase().trim());
  const cgpa = parseFloat(profile?.cgpa) || 0;
  const hasResume = !!profile?.has_resume;
  const classifiedRole = (profile?.classified_role || '').toLowerCase().trim();

  if (skills.length === 0 || !companies?.length) {
    return [
      { name: 'Google',        tag: 'SWE',         score: null },
      { name: 'Goldman Sachs', tag: 'Finance',      score: null },
      { name: 'McKinsey',      tag: 'Consulting',   score: null },
      { name: 'BCG Gamma',     tag: 'Data Science', score: null },
      { name: 'L&T',           tag: 'Core',         score: null },
      { name: 'Figma',         tag: 'Design',       score: null },
    ];
  }

  const results = [];
  for (const company of companies) {
    const matchedCount = (company.keywords || []).filter(kw =>
      skills.some(s => s === kw)
    ).length;
    const roleMatches = (company.roleKeys || []).some(rk => classifiedRole.includes(rk));
    if (matchedCount === 0 && !roleMatches) continue;
    const skillScore = Math.min((matchedCount / 4) * 65, 65);
    const cgpaScore = Math.min((cgpa / 10) * 10, 10);
    const expScore = hasResume ? 15 : 0;
    const roleBonus = roleMatches ? 10 : 0;
    const total = Math.round(Math.min(skillScore + cgpaScore + expScore + roleBonus, 99));
    if (total >= 15) results.push({ name: company.name, tag: company.tag, score: total });
  }
  return results.sort((a, b) => b.score - a.score).slice(0, 16);
}


function computeCompleteness(profile) {
  if (!profile) return { pct: 0, checks: {}, missing: [] };
  const checks = {
    basic_info: !!(profile.name && profile.college),
    resume_uploaded: !!profile.has_resume,
    skills_added: (profile.extracted_skills || []).length >= 3,
    role_detected: !!(profile.classified_role),
    preferences_filled: !!(profile.preferred_role_type),
  };
  const labels = {
    basic_info: "Basic info complete",
    resume_uploaded: "Resume uploaded",
    skills_added: "3+ skills added",
    role_detected: "Role detected or entered",
    preferences_filled: "Preferences filled",
  };
  const missing = Object.entries(checks).filter(([, v]) => !v).map(([k]) => labels[k]);
  const pct = Object.values(checks).filter(Boolean).length * 20;
  return { pct, checks, missing };
}

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { config } = useConfig();
  const navigate = useNavigate();

  const PREDEFINED_SKILLS = config.skills;
  const ROLE_TYPES = config.role_types;
  const COMPANY_TYPES = config.company_types;
  const LOCATIONS = config.locations;
  const TESTABLE_SKILLS = config.testable_skills;

  const [companies, setCompanies] = useState([]);
  const [profile, setProfile] = useState(null);
  const [score, setScore] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editSkills, setEditSkills] = useState([]);
  const [editPrefRoleType, setEditPrefRoleType] = useState("");
  const [editPrefCompanyTypes, setEditPrefCompanyTypes] = useState([]);
  const [editPrefLocations, setEditPrefLocations] = useState([]);
  const [editOpenInternships, setEditOpenInternships] = useState(true);
  const [skillSearch, setSkillSearch] = useState("");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDropdownRef = useRef(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeMsg, setResumeMsg] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showCodeChallenge, setShowCodeChallenge] = useState(false);
  const [activeChallengeId, setActiveChallengeId] = useState(null);
  const [showInterview, setShowInterview] = useState(false);
  const [challenges, setChallenges] = useState([]);

  // Theme-aware styles
  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const headerBg = isDark ? 'rgba(10,10,15,0.9)' : 'rgba(255,255,255,0.95)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const cardShadow = isDark ? 'none' : '0 1px 8px rgba(0,0,0,0.05)';
  const glassCard = { background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow };
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)' };
  const accent = '#059669';
  const accentDark = '#047857';
  const accentLight = isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.08)';
  const accentBorder = 'rgba(5,150,105,0.25)';

  const t = { glassCard, inputStyle, accent, accentDark, accentLight, accentBorder, textPrimary, textSecondary, textMuted, bg: pageBg, cardBg, border: cardBorder };

  const isTestable = (s) => TESTABLE_SKILLS.includes(s.toLowerCase());
  const getSkillStatus = (skillName) => {
    const vs = (profile?.verified_skills || []).find(v => v.skill_name?.toLowerCase() === skillName.toLowerCase() || v.skill_key === skillName.toLowerCase());
    return vs?.status || null;
  };
  const skillBadge = (skillName) => {
    if (!isTestable(skillName)) return null;
    const st = getSkillStatus(skillName);
    if (st === "verified")   return { symbol: "✓", color: "#10b981", bg: "rgba(16,185,129,0.15)" };
    if (st === "partial")    return { symbol: "~", color: "#f59e0b", bg: "rgba(245,158,11,0.15)" };
    return                          { symbol: "!", color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)", bg: "transparent" };
  };

  useEffect(() => {
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", "/student/dashboard");
  }, []);

  useEffect(() => {
    Promise.all([
      API.get("/api/student/profile").then(r => setProfile(r.data)),
      API.get("/api/student/placement-score").then(r => setScore(r.data)),
      API.get("/api/student/applications").then(r => setApplications(r.data)),
    ]).finally(() => setLoadingProfile(false));
  }, []);

  useEffect(() => {
    if (showVerifyModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showVerifyModal]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    API.get("/api/assessments/challenges").then(r => setChallenges(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    API.get("/api/config/companies").then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); window.location.replace("/"); };

  const openEdit = () => {
    setEditName(profile?.name || "");
    setEditPhone(profile?.phone || "");
    setEditRole(profile?.classified_role || "");
    setEditSkills(profile?.extracted_skills || []);
    setEditPrefRoleType(profile?.preferred_role_type || "");
    setEditPrefCompanyTypes(profile?.preferred_company_types || []);
    setEditPrefLocations(profile?.preferred_locations || []);
    setEditOpenInternships(profile?.open_to_internships ?? true);
    setEditError("");
    setResumeMsg("");
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setEditError("");
    try {
      await API.post("/api/student/profile", {
        name: editName,
        phone: editPhone,
        college: profile?.college || "",
        branch: profile?.branch || "",
        cgpa: profile?.cgpa || 0,
        graduation_year: profile?.graduation_year || 2026,
        classified_role: editRole,
        extracted_skills: editSkills,
        ai_summary: profile?.ai_summary || "",
        preferred_role_type: editPrefRoleType,
        preferred_company_types: editPrefCompanyTypes,
        preferred_locations: editPrefLocations,
        open_to_internships: editOpenInternships,
        open_to_ppo: true,
      });
      const fresh = await API.get("/api/student/profile");
      setProfile(fresh.data);
      const freshScore = await API.get("/api/student/placement-score");
      setScore(freshScore.data);
      setEditMode(false);
    } catch (err) {
      setEditError(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResumeReupload = async (file) => {
    if (!file) return;
    setResumeUploading(true);
    setResumeMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post("/api/student/upload-resume", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.extracted_skills?.length > 0) setEditSkills(res.data.extracted_skills);
      if (res.data.classified_role) setEditRole(res.data.classified_role);
      setProfile(prev => prev ? { ...prev, has_resume: true } : prev);
      setResumeMsg("Resume uploaded! Skills updated from AI.");
    } catch (err) {
      setResumeMsg(formatError(err));
    } finally {
      setResumeUploading(false);
    }
  };

  const filteredSkills = PREDEFINED_SKILLS.filter(
    s => s.toLowerCase().includes(skillSearch.toLowerCase()) && !editSkills.includes(s)
  ).slice(0, 8);

  const toggleMultiSelect = (value, list, setList) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const [companyMatches, matchError] = useMemo(() => {
    if (!profile) return [[], false];
    try { return [getCompanyMatches(profile, companies), false]; }
    catch (e) { return [[], true]; }
  }, [profile, companies]);

  if (loadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: pageBg }}>
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
      </div>
    );
  }
  const { pct, missing } = computeCompleteness(profile);

  return (
    <div className="min-h-screen" style={{ background: pageBg }}>
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: headerBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: `1px solid ${headerBorder}` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-xs">W</span>
            </div>
            <span className="font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>WonStepCareer</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:block" style={{ color: textMuted }}>Hi, {user?.name || "Student"}</span>
            <button
              onClick={e => toggleTheme(e)}
              aria-label="Toggle theme"
              style={{ width: '30px', height: '30px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary }}
            >
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
            </button>
            {!editMode && (
              <Button variant="outline" size="sm" onClick={openEdit} className="gap-2" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }} data-testid="edit-profile-btn">
                <Edit2 size={14} /> Edit Profile
              </Button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="dashboard-logout-btn"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
            Welcome back, {profile?.name || user?.name}
          </h1>
          <p className="mt-1 text-sm" style={{ color: textMuted }}>Here's your placement overview</p>
        </div>

        {/* Profile Completeness */}
        <Card className="mb-6 glass-card" style={glassCard} data-testid="completeness-card">
          <CardContent className="py-4 px-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} style={{ color: accent }} />
                <span className="text-sm font-semibold" style={{ color: textPrimary }}>Profile Completeness</span>
              </div>
              <span className="text-sm font-bold" style={{ color: accent }}>{pct}%</span>
            </div>
            <Progress value={pct} className="h-2 mb-2" />
            {pct === 100 ? (
              <p className="text-xs font-medium mt-1" style={{ color: accent }}>Your profile is complete! You're visible to recruiters.</p>
            ) : (
              <p className="text-xs mt-1" style={{ color: textMuted }}>Open <strong>Edit Profile</strong> to fill in missing fields and improve your match rate.</p>
            )}
          </CardContent>
        </Card>

        {/* Edit Mode Panel */}
        {editMode && (
          <Card className="mb-6 shadow-lg" style={{ background: cardBg, backdropFilter: 'blur(20px)', border: `1px solid ${accentBorder}`, boxShadow: cardShadow }} data-testid="edit-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Edit Profile</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)} style={{ color: textMuted }}>
                  <X size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm" style={{ color: textSecondary }}>Full Name</Label>
                  <Input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Enter your full name"
                    className="mt-1"
                    style={{ ...inputStyle, color: textPrimary, ...(!editName ? { borderColor: '#dc2626', borderWidth: '1px', borderStyle: 'solid' } : {}) }}
                    data-testid="edit-name"
                  />
                </div>
                <div>
                  <Label className="text-sm" style={{ color: textSecondary }}>Phone</Label>
                  <Input
                    value={editPhone}
                    onChange={e => setEditPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    className="mt-1"
                    style={{ ...inputStyle, color: textPrimary, ...(!editPhone ? { borderColor: '#dc2626', borderWidth: '1px', borderStyle: 'solid' } : {}) }}
                    data-testid="edit-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm" style={{ color: textSecondary }}>Preferred Role</Label>
                <Input
                  value={editRole}
                  onChange={e => setEditRole(e.target.value)}
                  placeholder="Enter your preferred role (e.g. Software Engineer)"
                  className="mt-1"
                  style={{ ...inputStyle, color: textPrimary, ...(!editRole ? { borderColor: '#dc2626', borderWidth: '1px', borderStyle: 'solid' } : {}) }}
                  data-testid="edit-role"
                />
              </div>

              <div>
                <Label className="text-sm" style={{ color: textSecondary }}>Skills</Label>
                <div ref={skillDropdownRef} className="relative mt-1">
                  <Input
                    value={skillSearch}
                    onChange={e => { setSkillSearch(e.target.value); setShowSkillDropdown(true); }}
                    onFocus={() => setShowSkillDropdown(true)}
                    placeholder="Search and add skills..."
                    style={{ ...inputStyle, color: textPrimary }}
                    data-testid="edit-skill-input"
                  />
                  {showSkillDropdown && filteredSkills.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 rounded-lg shadow-2xl max-h-40 overflow-auto" style={{ background: isDark ? '#0d0d14' : '#ffffff', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)' }}>
                      {filteredSkills.map(skill => (
                        <button
                          key={skill}
                          type="button"
                          onMouseDown={e => { e.preventDefault(); setEditSkills(prev => [...prev, skill]); setSkillSearch(""); setShowSkillDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm"
                          style={{ color: textSecondary }}
                        >{skill}</button>
                      ))}
                    </div>
                  )}
                </div>
                {editSkills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editSkills.map(s => (
                      <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ background: isDark ? accentLight : accent, borderColor: accentBorder, color: isDark ? '#34d399' : '#ffffff' }}>
                        {s}
                        <button onClick={() => setEditSkills(editSkills.filter(x => x !== s))} className="ml-1 hover:text-red-300"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm" style={{ color: textSecondary }}>Resume</Label>
                <div
                  className="mt-1 rounded-xl p-4 text-center cursor-pointer transition-colors"
                  onClick={() => document.getElementById("edit-resume-input").click()}
                  style={{ border: !profile?.has_resume ? '2px dashed #dc2626' : isDark ? '2px dashed rgba(255,255,255,0.12)' : '2px dashed rgba(0,0,0,0.1)', background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                  data-testid="edit-resume-zone"
                >
                  <input id="edit-resume-input" type="file" accept=".pdf" className="hidden" onChange={e => handleResumeReupload(e.target.files[0])} />
                  {resumeUploading ? (
                    <div className="flex items-center justify-center gap-2" style={{ color: accent }}>
                      <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
                      <span className="text-sm">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2" style={{ color: textMuted }}>
                      <Upload size={16} />
                      <span className="text-sm">{profile?.has_resume ? "Re-upload Resume (PDF)" : "Upload Resume (PDF)"}</span>
                    </div>
                  )}
                </div>
                {resumeMsg && (
                  <p className={`text-xs mt-1 ${resumeMsg.includes("uploaded") ? "text-green-500" : "text-red-500"}`}>{resumeMsg}</p>
                )}
              </div>

              <div className="border-t pt-4" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                <p className="text-sm font-semibold mb-3" style={{ color: textPrimary }}>Preferences</p>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm" style={{ color: textSecondary }}>Preferred Role Type</Label>
                    <select
                      value={editPrefRoleType}
                      onChange={e => setEditPrefRoleType(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-md text-sm focus:outline-none"
                      style={{ ...inputStyle, color: textPrimary, border: !editPrefRoleType ? '1px solid #dc2626' : isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)' }}
                    >
                      <option value="" style={{ background: isDark ? '#0d0d14' : '#ffffff' }}>Select...</option>
                      {ROLE_TYPES.map(r => <option key={r} value={r} style={{ background: isDark ? '#0d0d14' : '#ffffff' }}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm block mb-2" style={{ color: textSecondary }}>Company Types</Label>
                    <div className="flex flex-wrap gap-2">
                      {COMPANY_TYPES.map(ct => (
                        <button key={ct} type="button" onClick={() => toggleMultiSelect(ct, editPrefCompanyTypes, setEditPrefCompanyTypes)}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                          style={editPrefCompanyTypes.includes(ct)
                            ? { background: `linear-gradient(135deg, ${accent}, ${accentDark})`, borderColor: accent, color: 'white' }
                            : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', color: textSecondary }
                          }>
                          {ct}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm block mb-2" style={{ color: textSecondary }}>Preferred Locations</Label>
                    <div className="flex flex-wrap gap-2">
                      {LOCATIONS.map(loc => (
                        <button key={loc} type="button" onClick={() => toggleMultiSelect(loc, editPrefLocations, setEditPrefLocations)}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                          style={editPrefLocations.includes(loc)
                            ? { background: accentLight, borderColor: accentBorder, color: accent }
                            : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', color: textSecondary }
                          }>
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl" style={{ border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)', background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: textPrimary }}>Open to Internships?</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEditOpenInternships(true)} className="flex-1 py-1 rounded text-xs font-medium border transition-colors"
                        style={editOpenInternships ? { background: accentLight, borderColor: accentBorder, color: accent } : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', color: textMuted }}>Yes</button>
                      <button type="button" onClick={() => setEditOpenInternships(false)} className="flex-1 py-1 rounded text-xs font-medium border transition-colors"
                        style={!editOpenInternships ? { background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.4)', color: '#dc2626' } : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', color: textMuted }}>No</button>
                    </div>
                  </div>
                </div>
              </div>

              {editError && <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>{editError}</p>}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditMode(false)} className="flex-1" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}>Cancel</Button>
                <Button type="button" onClick={handleSaveEdit} disabled={saving} className="flex-1 font-semibold gap-2" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="save-profile-btn">
                  <Check size={16} /> {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Card (full width — score card removed) */}
        <div className="mb-6">
          <Card className="glass-card" style={glassCard} data-testid="profile-card">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
                    {profile?.name || user?.name}
                  </CardTitle>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>{user?.email}</p>
                  {profile?.phone && <p className="text-xs mt-0.5" style={{ color: textMuted }}>{profile.phone}</p>}
                </div>
                {profile?.classified_role && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="profile-role-badge">
                    {profile.classified_role}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {profile ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                    <div className="flex items-center gap-2">
                      <GraduationCap size={16} style={{ color: accent }} className="flex-shrink-0" />
                      <div>
                        <p className="text-xs" style={{ color: textMuted }}>College</p>
                        <p className="text-sm font-medium truncate" style={{ color: textPrimary }}>{profile.college}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase size={16} style={{ color: accent }} className="flex-shrink-0" />
                      <div>
                        <p className="text-xs" style={{ color: textMuted }}>Branch</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{profile.branch}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award size={16} style={{ color: accent }} className="flex-shrink-0" />
                      <div>
                        <p className="text-xs" style={{ color: textMuted }}>CGPA</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{profile.cgpa} / 10</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} style={{ color: accent }} className="flex-shrink-0" />
                      <div>
                        <p className="text-xs" style={{ color: textMuted }}>Graduation</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{profile.graduation_year}</p>
                      </div>
                    </div>
                  </div>

                  {profile.ai_summary && (
                    <div className="p-3 rounded-lg mb-4" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                      <p className="text-xs font-medium mb-1" style={{ color: textMuted }}>AI Summary</p>
                      <p className="text-sm" style={{ color: textSecondary }}>{profile.ai_summary}</p>
                    </div>
                  )}

                  {profile.extracted_skills?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: textMuted }}>Top Skills</p>
                        <button
                          onClick={() => setShowVerifyModal(true)}
                          style={{fontSize:'12px',fontWeight:'500',padding:'2px 8px',borderRadius:'9999px',cursor:'pointer',color:accent,background:accentLight,border:`1px solid ${accentBorder}`}}
                        >
                          Verify Skills
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {profile.extracted_skills.map(s => {
                          const badge = skillBadge(s);
                          return (
                            <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border"
                              style={{ background: isDark ? accentLight : accent, borderColor: accentBorder, color: isDark ? '#34d399' : '#ffffff' }}
                              data-testid={`dashboard-skill-${s}`}>
                              {s}
                              {badge && (
                                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold leading-none"
                                  style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}` }}>
                                  {badge.symbol}
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {profile.preferred_role_type && (
                    <div>
                      <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: textMuted }}>Preferences</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 rounded-full" style={{ background: isDark ? accentLight : accent, color: isDark ? '#34d399' : '#ffffff' }}>{profile.preferred_role_type}</span>
                        {profile.open_to_internships && <span className="px-2 py-1 rounded-full" style={{ background: isDark ? accentLight : accent, color: isDark ? '#34d399' : '#ffffff' }}>Open to Internships</span>}
                        {(profile.preferred_locations || []).slice(0, 2).map(l => (
                          <span key={l} className="px-2 py-1 rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', color: textSecondary }}>{l}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-6 text-center">
                  <User size={32} className="mx-auto mb-3" style={{ color: textMuted }} />
                  <p className="text-sm mb-4" style={{ color: textSecondary }}>Profile not yet complete.</p>
                  <Button size="sm" className="font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} onClick={() => navigate("/student/signup")}>
                    Complete Profile
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Company Match Predictions */}
        <Card className="mb-6 glass-card" style={glassCard} data-testid="company-matches-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 size={18} style={{ color: accent }} />
              <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Company Match Predictions</CardTitle>
            </div>
            <p className="text-xs mt-1" style={{ color: textMuted }}>Based on your detected skills — sorted by fit score</p>
          </CardHeader>
          <CardContent>
            {matchError ? (
              <p className="text-sm text-center py-4" style={{ color: textMuted }}>Calculating your matches — check back soon</p>
            ) : (
              <div className="space-y-3">
                {companyMatches.map(company => {
                  const ts = getTagStyle(company.tag, isDark);
                  return (
                    <div key={company.name} className="flex items-center gap-4 p-3 rounded-lg transition-colors" style={{ cursor: 'default' }}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium" style={{ color: textPrimary }}>{company.name}</span>
                          <span className="text-sm font-bold ml-2" style={{ color: company.score === null ? textMuted : accent }}>
                            {company.score === null ? '—' : `${company.score}%`}
                          </span>
                        </div>
                        <Progress value={company.score ?? 0} className="h-1.5" />
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0" style={{ background: ts.bg, color: ts.text, border: `1px solid ${ts.border}` }}>
                        {company.tag}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Application Tracker */}
        <Card className="mb-6 glass-card" style={glassCard} data-testid="applications-table-card">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>My Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <ApplicationTimeline applications={applications} isDark={isDark} t={t} />
          </CardContent>
        </Card>

        {/* Code Assessments */}
        {challenges.length > 0 && (
          <Card className="mb-6 glass-card" style={glassCard} data-testid="code-assessments-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Code Assessments</CardTitle>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium border" style={{ background: accentLight, borderColor: accentBorder, color: accent }}>{challenges.length} available</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {challenges.slice(0, 3).map(ch => (
                  <div key={ch.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                    <div>
                      <span className="text-sm font-medium" style={{ color: textPrimary }}>{ch.title}</span>
                      <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ background: ch.difficulty === 'hard' ? 'rgba(239,68,68,0.12)' : ch.difficulty === 'easy' ? 'rgba(5,150,105,0.12)' : 'rgba(5,150,105,0.12)', color: ch.difficulty === 'hard' ? '#dc2626' : ch.difficulty === 'easy' ? '#059669' : '#059669' }}>{ch.difficulty}</span>
                    </div>
                    <Button size="sm" className="text-xs gap-1" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} onClick={() => { setActiveChallengeId(ch.id); setShowCodeChallenge(true); }} data-testid={`start-challenge-${ch.id}`}>
                      Attempt
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Interview */}
        <Card className="mb-6 glass-card" style={glassCard} data-testid="interview-prep-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock size={18} style={{ color: accent }} />
              <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>AI Mock Interview</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-center py-4">
            <p className="text-sm mb-4" style={{ color: textSecondary }}>Practice with our AI interviewer. Get scored on communication, STAR format, and role relevance.</p>
            <Button className="font-semibold gap-2" style={{ background: `linear-gradient(135deg, #1a1f5e, #2d3494)`, color: 'white' }} onClick={() => window.open('/student/interview', '_blank')} data-testid="start-interview-btn">
              Start Interview
            </Button>
          </CardContent>
        </Card>
      </main>

      {showCodeChallenge && activeChallengeId && (() => {
        const ch = challenges.find(c => c.id === activeChallengeId);
        if (!ch) return null;
        return (
          <div onClick={() => setShowCodeChallenge(false)} style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9998,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}>
            <div onClick={e => e.stopPropagation()} style={{ width:'100%',maxWidth:'800px',maxHeight:'90vh',overflowY:'auto',borderRadius:'16px',background:isDark?'#1a1f2e':'#ffffff',boxShadow:'0 20px 60px rgba(0,0,0,0.4)',border:isDark?'1px solid rgba(255,255,255,0.1)':'none',padding:'24px' }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px' }}>
                <h2 style={{ margin:0,fontSize:'18px',fontWeight:'700',color:isDark?'#fff':'#111' }}>Code Challenge</h2>
                <button onClick={() => setShowCodeChallenge(false)} style={{ background:'none',border:'none',fontSize:'22px',cursor:'pointer',color:isDark?'rgba(255,255,255,0.5)':'#6b7280' }}>×</button>
              </div>
              <CodeChallenge challenge={ch} isDark={isDark} t={t} onSubmitSuccess={() => setShowCodeChallenge(false)} />
            </div>
          </div>
        );
      })()}


      {showVerifyModal && createPortal(
        <div
          onClick={() => setShowVerifyModal(false)}
          style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.65)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',overscrollBehavior:'none'}}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{width:'90%',maxWidth:'480px',maxHeight:'calc(100vh - 32px)',overflowY:'auto',borderRadius:'16px',padding:'24px',background:isDark?'#1a1f2e':'#ffffff',boxShadow:'0 20px 60px rgba(0,0,0,0.4)',border:isDark?'1px solid rgba(255,255,255,0.1)':'none'}}
          >
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
              <h2 style={{fontSize:'20px',fontWeight:'700',color:isDark?'#ffffff':'#111827',margin:0}}>Verify Your Skills</h2>
              <button
                onClick={() => setShowVerifyModal(false)}
                style={{background:'none',border:'none',fontSize:'24px',lineHeight:1,cursor:'pointer',color:isDark?'rgba(255,255,255,0.5)':'#6b7280',padding:'4px'}}
              >×</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {(profile?.extracted_skills || []).filter(s => isTestable(s)).length === 0 ? (
                <p style={{color:isDark?'rgba(255,255,255,0.5)':'#6b7280',textAlign:'center',padding:'24px 0',margin:0}}>No testable skills found. Add Python, DSA, JavaScript, or SQL to verify.</p>
              ) : (
                (profile.extracted_skills || []).filter(s => isTestable(s)).map((skill) => {
                  const status = getSkillStatus(skill);
                  return (
                    <div
                      key={skill}
                      style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 14px',borderRadius:'10px',border:isDark?'1px solid rgba(255,255,255,0.1)':'1px solid #e5e7eb',background:isDark?'rgba(255,255,255,0.04)':'#f9fafb'}}
                    >
                      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                        <span style={{fontWeight:'600',color:isDark?'#ffffff':'#111827',fontSize:'14px'}}>{skill}</span>
                        {status === 'verified' && (
                          <span style={{fontSize:'11px',fontWeight:'600',color:'#059669',background:isDark?'rgba(5,150,105,0.2)':'#d1fae5',padding:'2px 8px',borderRadius:'999px'}}>✓ Verified</span>
                        )}
                        {status === 'partial' && (
                          <span style={{fontSize:'11px',fontWeight:'600',color:'#d97706',background:isDark?'rgba(245,158,11,0.2)':'#fef3c7',padding:'2px 8px',borderRadius:'999px'}}>Partial</span>
                        )}
                      </div>
                      <a
                        href={'/student/skill-test/' + encodeURIComponent(skill)}
                        style={{padding:'6px 14px',borderRadius:'8px',background:status==='verified'?(isDark?'rgba(255,255,255,0.1)':'#e5e7eb'):'#059669',color:status==='verified'?(isDark?'rgba(255,255,255,0.4)':'#6b7280'):'#ffffff',fontWeight:'600',fontSize:'13px',textDecoration:'none',display:'inline-block',flexShrink:0}}
                      >
                        {status === 'verified' ? 'Done' : 'Take Test'}
                      </a>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
